import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { BRAND_COLOR, CI_SRC, COMPANY } from '@/lib/brand';
import { groupDigits } from '@/lib/money';
import { materialItemAmount, type MaterialItem } from '@/pages/approval/formSchema';
import { BudgetApprovalBoxes, BudgetSheetTitle, OverseasBudgetSheet } from '@/pages/approval/OverseasBudgetSheet';
import type { OverseasBudget } from '@/pages/approval/overseasBudget';
import { paperBoxes, stampDate, type ApprovalBox, type PaperCell } from '@/pages/approval/paperForm';

/**
 * 전자결재 기안문 — <b>사내 표준 레터헤드 양식 그대로</b> (DOC-51).
 *
 * 사내 결재는 두 겹이다. 팀장까지는 전자결재로 끝나고 <b>본부장·대표이사는 종이로</b> 올라간다.
 * 그래서 결재란은 항상 네 칸(대표이사·본부장·팀장·담당)을 그리고, 전자결재가 끝낸 칸에만
 * 도장을 찍는다 — <b>남은 빈 칸이 곧 "여기부터 서면"</b> 이라는 표시다. 배치 규칙은
 * {@link paperBoxes} 한 곳에 있다.
 *
 * 화면과 인쇄가 <b>같은 컴포넌트</b>다. 그래서 인라인 스타일·실색상만 쓴다(테마 변수 X) —
 * {@link printDocument} 가 이 노드의 outerHTML 을 새 창에 그대로 실어도 모양이 유지된다.
 * 로고도 CSS 배경이 아니라 `<img>` 다(배경 이미지는 브라우저 인쇄 설정에 따라 사라진다).
 */

export type { ApprovalBox };

/**
 * 종이의 한 칸 — 라벨과 값.
 *
 * <p>{@link DocField.input} 이 있으면 값 자리에 <b>그것</b>이 선다. 기안 화면이 이 종이를 그대로
 * 입력면으로 쓰는 길이다({@link DocView.edit}) — 읽기 전용으로 부르는 쪽(결재함·인쇄)은 이 칸을
 * 넘기지 않으므로 종이는 한 글자도 달라지지 않는다.
 */
export type DocField = { label: string; value: string; input?: React.ReactNode };

/**
 * <b>미리보기가 곧 입력면이다</b> (사용자 지시 2026-09-29 — 「미리보기 화면을 입력창 그대로 가져와서
 * 입력할 수 있도록」).
 *
 * <p>기안 화면은 예전에 왼쪽에 입력 카드들을, 모달에 종이를 따로 두었다. 같은 문서를 두 번 그린
 * 셈이라 <b>어디에 적은 글이 종이의 어디로 가는지</b>를 미리보기를 열어 봐야 알 수 있었다.
 * 이제 종이 한 장만 그리고, 사람이 적는 자리에만 칸을 세운다.
 *
 * <p>여기 없는 자리(문서번호·시행일자·결재란·발신명의)는 <b>사람이 정하지 않는 값</b>이라 종이
 * 그대로 남는다 — 결재란은 규정이 세우고, 고치려면 머리의 [결재선]으로 간다.
 */
export type DocEdit = {
  /** 「제 목」 줄. */
  title?: React.ReactNode;
  /** 「수 신」 줄 — 서식이 수신 칸을 가진 경우(협조전). */
  recipient?: React.ReactNode;
  /** 「참 조」 줄 — 이름표와 [지정]. */
  references?: React.ReactNode;
  /** 자유 서술 본문. */
  body?: React.ReactNode;
  /** 「협 조 전 | 비고」 표를 대신하는 입력 표. */
  rows?: React.ReactNode;
  /** 품목 표({@link DocView.itemTable})를 대신하는 입력 표. */
  itemTable?: React.ReactNode;
  /** 예산표({@link DocView.budget})를 대신하는 입력 표. */
  budget?: React.ReactNode;
};

export type DocView = {
  /** 서식명(연차 사용 신청서 등). 자유기안이면 비워 둔다. */
  formName: string;
  title: string;
  docNo: string | null;
  /** 시행일자 — `2026. 08. 10.` 형태. */
  dateLabel: string;
  drafterName: string;
  drafterOrg: string | null;
  /** 기안자 직급 — 양식 표의 「직급」 칸. */
  drafterRank?: string | null;
  urgency: string;
  boxes: ApprovalBox[];
  fields: DocField[];
  longs: DocField[];
  /**
   * 번호가 붙는 항목 표 — 「내용 / 기한 / 비고」. 번호는 값이 아니라 <b>그릴 때 매기는 자리</b>다.
   */
  rows?: Array<{ label: string; items: Array<{ content: string; due: string; note: string }> }>;
  /**
   * 품목 표 — 「품목 · 규격 · 수량 · 단가 · 금액」 과 합계(잡자재 · DOC-139). 금액은 그릴 때 센다.
   */
  itemTable?: { label: string; items: MaterialItem[]; total: number } | null;
  /**
   * 해외 경비 예산표 — 있으면 「- 아 래 -」 항목 표 대신 <b>엑셀 양식 그대로의 표</b>가 본문이다(2026-11-02).
   */
  budget?: OverseasBudget | null;
  body: string | null;
  /** 수신 — 사내 품의는 '내부품의'. */
  recipient?: string | null;
  /** 참조자 이름. 결재란이 아니라 머리에 선다. */
  references?: string[];
  /** 붙임(첨부 파일명). */
  attachments?: string[];
  /**
   * 결재란을 <b>어느 양식으로</b> 그리는가 (사용자 지시 2026-09-09 — 「샘플에 맞게 해」).
   *
   * <p>{@code STANDARD} 는 사내 품의 양식(DOC-51)이다 — 가로로 늘어선 대표이사 / 본부장 / 팀장 /
   * 담당 + 협조. {@code COOPERATION} 은 <b>협조전 원본 양식</b>이다(docs/samples/협조전 의 표 셀
   * 좌표에서 그대로 옮겼다): 왼쪽에 <b>세로로 쌓인</b> 임원 / 팀장 / 담당 / 기안, 오른쪽 위가
   * 대표이사, 오른쪽 아래가 협조. <b>본부장 칸은 없다.</b>
   *
   * <p>본문도 갈린다 — 협조전은 소속·이름·직급 머리도, 「- 아 래 -」도, 맺음말도 없이
   * 「협 조 전 | 비고」 표 하나가 본문이다({@link CooperationSheet}).
   *
   * <p>협조전에서 임원 칸은 <b>비어서</b>, 대표이사 칸에는 <b>팀장의 서명</b>이, 팀장 칸에는
   * <b>「전결」</b>이 나간다 — 팀장 전결로 끝나는 문서다({@link paperBoxes}).
   */
  boxLayout?: 'STANDARD' | 'COOPERATION';
  /** 있으면 이 종이는 <b>적는 종이</b>다 — 값 자리에 칸이 선다({@link DocEdit}). */
  edit?: DocEdit;
};

const INK = BRAND_COLOR.ink;
const LINE = BRAND_COLOR.line;

/**
 * A4 한 장의 속칸(mm) — <b>{@link printDocument} 의 `@page` 여백과 짝인 값</b>이다.
 * 210×297 에서 여백 18mm·15mm 를 뺀 174×267 이고, 267 대신 265 를 쓰는 것은 브라우저가 mm 를 픽셀로
 * 반올림하다 1mm 쯤 넘겨 <b>빈 둘째 장</b>을 뱉는 일을 막으려는 여유다.
 *
 * <p>화면은 이 속칸에 안쪽 여백을 더해 그린다. 그래서 <b>화면에서 잰 자리가 곧 종이의 자리</b>다 —
 * 인쇄 창에서는 `.bomi-doc` 의 패딩이 0 이 되고 폭이 174mm 로 앉으므로, 두 곳의 글 넓이·높이가 같다.
 * 한쪽 값만 고치면 미리보기에 다 들어가던 글이 인쇄에서 잘린다.
 *
 * <p>화면이 190mm 보다 좁으면(작은 창·모바일) 글이 더 자주 줄바꿈되어 종이보다 길어 보인다. 그때는
 * 필요보다 조금 더 줄여 넣는다 — 넘치는 쪽으로 틀리면 잘리지만, 줄이는 쪽으로 틀리면 작게 나올 뿐이다.
 */
const PAGE_W_MM = 174;
const PAGE_H_MM = 265;
const SCREEN_PAD_Y_MM = 10;
const SCREEN_PAD_X_MM = 8;

/** 줄여 넣기의 바닥 — 이보다 작아지면 읽을 수 없으므로 더 줄이지 않는다(그 아래는 잘린다). */
const MIN_FIT = 0.55;

/**
 * 해외 경비 예산표의 바닥 — 이 서식은 <b>무조건 한 장</b>이다(사용자 지시 2026-11-03). 줄이 늘면 잘라 내지 않고
 * 끝까지 줄여 넣는다. 0.3 아래로는 글자가 점이 되므로 그때만 잘린다(줄 수 백 개 남짓).
 */
const BUDGET_MIN_FIT = 0.3;

/** CSS 의 `mm` 을 px 로. 브라우저는 1in = 96px 로 잡는다. */
const PX_PER_MM = 96 / 25.4;
const PAGE_BOX_W = (PAGE_W_MM + SCREEN_PAD_X_MM * 2) * PX_PER_MM;
const PAGE_BOX_H = (PAGE_H_MM + SCREEN_PAD_Y_MM * 2) * PX_PER_MM;

/**
 * 모달 안에서 <b>한 장이 한 눈에</b> 들어오게 지면을 줄인다.
 *
 * A4 한 장은 285mm — 1000px 이 넘는다. 그대로 두면 미리보기가 스크롤 상자가 되어, 결재란을 보다가
 * 본문을 보려면 끌어내려야 하고 <b>한 장인지 두 장인지도 알 수 없다.</b> 종이를 보는 방식은 그것이 아니다.
 *
 * <p>남는 자리는 재서 쓴다. 폭은 이 액자의 실제 폭이고, 높이는 <b>모달이 커질 수 있는 최대 높이
 * ({@code max-height: 90vh})에서 문서를 뺀 나머지</b>(머리·바닥·같은 몸통의 다른 블록)를 뺀 값이다.
 * 상수로 "머리·바닥은 대충 200px" 이라고 적어 두면 그 밑에 무엇이 하나 붙는 순간 다시 스크롤이 생긴다.
 *
 * <p>줄이는 것은 <b>액자</b>이지 문서가 아니다. 인쇄는 안쪽 `.bomi-doc` 노드를 그대로 새 창에 싣는데
 * ({@link printDocument}), 문서에 배율을 걸어 두면 종이도 같이 작아진다.
 */
/**
 * @param widthOnly 너비에만 맞춘다 — 해외 경비 예산표처럼 표가 빽빽한 종이는 한 장 전체를 창 높이에 넣으면 글씨가 읽히지 않는다
 *                  (사용자 지시 2026-11-03 「미리보기 뜨면 너무 작게 보이네」). 그때는 위아래로 넘겨 본다.
 */
function usePageZoom(widthOnly = false) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState({ zoom: 1, room: 0 });

  useLayoutEffect(() => {
    const measure = () => {
      const frame = frameRef.current;
      if (!frame) return;
      const byWidth = frame.clientWidth > 0 ? frame.clientWidth / PAGE_BOX_W : 1;

      let room = 0;
      let byHeight = Infinity;
      const modal = frame.closest('.modal-content');
      if (modal instanceof HTMLElement) {
        const limit = parseFloat(getComputedStyle(modal).maxHeight);
        // 지금 그려진 전체 높이에서 이 액자를 뺀 것이 곧 「문서 아닌 것」 — 배율이 얼마든 같은 값이다.
        const others = modal.scrollHeight - frame.offsetHeight;
        room = (Number.isFinite(limit) ? limit : window.innerHeight * 0.9) - others;
        if (room > 0) byHeight = room / PAGE_BOX_H;
      }

      setFit({ zoom: Math.min(1, byWidth, widthOnly ? Infinity : byHeight), room });
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [widthOnly]);

  return [frameRef, fit] as const;
}

/**
 * 사람이 고르는 배율 — 자동맞춤은 <b>한 장이 다 보이는</b> 값이라 글씨가 작다. 읽으려면 키워야 한다.
 *
 * 자동맞춤을 지우지 않고 「맞춤」 으로 돌아오게 둔다. 확대해서 읽다 보면 지금 보는 것이 종이의 어디쯤인지
 * 잃어버리는데, 한 번에 전체로 돌아올 자리가 없으면 배율을 손으로 되돌려 맞춰야 한다.
 */
const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];
const MIN_ZOOM = 0.3;
const MAX_ZOOM = 4;

function stepZoom(current: number, direction: 1 | -1): number {
  const next = direction > 0
    ? ZOOM_STEPS.find((step) => step > current + 0.001)
    : [...ZOOM_STEPS].reverse().find((step) => step < current - 0.001);
  return next ?? current;
}

/**
 * 뷰어처럼 다루기 — <b>휠로 키우고, 끌어서 옮긴다.</b> 단추만으로도 되지만, 확대한 종이를 읽으려면
 * 스크롤막대를 찾아 잡아야 해서 종이를 보는 동작과 다르다.
 *
 * <h3>휠은 종이 위에서만 배율이다</h3>
 * 액자 전체가 아니라 <b>종이(무대)</b>에 듣는다. 종이 옆 여백에서는 휠이 여느 때처럼 모달을 굴려,
 * 문서 아래의 첨부·이력으로 내려갈 길이 남는다. 종이 위에서 다 잡아 버리면 그 길이 사라진다.
 *
 * <p>리스너를 손으로 붙이는 이유: React 의 {@code onWheel} 은 passive 로 달려
 * {@code preventDefault} 가 듣지 않는다 — 배율이 바뀌면서 모달까지 함께 굴러간다.
 *
 * <h3>커서 아래를 붙잡는다</h3>
 * 배율만 바꾸면 보고 있던 곳이 화면 밖으로 밀려난다. 휠을 굴린 지점의 종이 좌표가 <b>제자리에 남도록</b>
 * 스크롤을 같이 옮긴다. 배율이 그려진 <b>뒤</b>에 옮겨야 하므로 {@code useLayoutEffect} 로 미룬다.
 *
 * <h3>끌기는 넘칠 때만</h3>
 * 종이가 액자에 다 들어와 있으면 끌 곳이 없다 — 그때도 끌기를 잡으면 <b>글자를 고르지 못한다.</b>
 */
function usePageViewer(
  frameRef: React.RefObject<HTMLDivElement | null>,
  zoom: number,
  setZoom: (next: number | ((prev: number | null) => number | null)) => void,
) {
  const paperRef = useRef<HTMLDivElement>(null);
  const anchor = useRef<{ x: number; y: number; ratio: number } | null>(null);
  const grab = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const [panning, setPanning] = useState(false);

  useEffect(() => {
    const paper = paperRef.current;
    const frame = frameRef.current;
    if (!paper || !frame) return;

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const factor = event.deltaY < 0 ? 1.12 : 1 / 1.12;
      const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom * factor));
      if (next === zoom) return;
      const box = frame.getBoundingClientRect();
      anchor.current = { x: event.clientX - box.left, y: event.clientY - box.top, ratio: next / zoom };
      setZoom(next);
    };

    paper.addEventListener('wheel', onWheel, { passive: false });
    return () => paper.removeEventListener('wheel', onWheel);
  }, [frameRef, zoom, setZoom]);

  useLayoutEffect(() => {
    const frame = frameRef.current;
    const held = anchor.current;
    anchor.current = null;
    if (!frame || !held) return;
    frame.scrollLeft = (frame.scrollLeft + held.x) * held.ratio - held.x;
    frame.scrollTop = (frame.scrollTop + held.y) * held.ratio - held.y;
  }, [frameRef, zoom]);

  useEffect(() => {
    if (!panning) return;
    const move = (event: MouseEvent) => {
      const frame = frameRef.current;
      const from = grab.current;
      if (!frame || !from) return;
      frame.scrollLeft = from.left - (event.clientX - from.x);
      frame.scrollTop = from.top - (event.clientY - from.y);
    };
    const stop = () => setPanning(false);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', stop);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', stop);
    };
  }, [frameRef, panning]);

  const onMouseDown = (event: React.MouseEvent) => {
    const frame = frameRef.current;
    if (!frame || event.button !== 0) return;
    const overflows =
      frame.scrollHeight > frame.clientHeight + 1 || frame.scrollWidth > frame.clientWidth + 1;
    if (!overflows) return;
    event.preventDefault(); // 끌기가 글자 선택으로 새지 않게
    grab.current = { x: event.clientX, y: event.clientY, left: frame.scrollLeft, top: frame.scrollTop };
    setPanning(true);
  };

  return { paperRef, panning, onMouseDown };
}

/**
 * 본문이 A4 한 장을 넘으면 <b>그만큼 줄여 넣는다</b> — 둘째 장으로 넘기지 않는다.
 *
 * 결재 문서는 한 장이 곧 한 건이다. 두 장이 되면 결재란이 있는 첫 장과 「끝.」 이 있는 둘째 장이 따로 다니고,
 * 서면으로 올릴 때 둘째 장만 빠지는 일이 실제로 생긴다. 그래서 넘칠 때는 잘라 내는 대신 축소한다 —
 * 종이 문서에서 사람이 하던 것과 같다.
 *
 * <p>재는 값은 두 개뿐이다: 남는 자리({@code flow.clientHeight})와 글이 필요로 하는 높이
 * ({@code fit.scrollHeight}). 줄일 때 <b>폭을 {@code 1/배율} 만큼 늘려</b> 두어, 축소된 뒤에도 글이
 * 종이 폭을 그대로 채우게 한다(줄만 줄이면 오른쪽에 빈 띠가 생긴다).
 *
 * <p>상태가 아니라 DOM 스타일을 직접 만진다. 인쇄는 이 노드의 `outerHTML` 을 그대로 새 창에 싣는데
 * ({@link printDocument}), 인라인 스타일이라야 그 축소가 종이까지 따라간다.
 */
function useFitToPage(view: DocView, enabled: boolean) {
  const flowRef = useRef<HTMLDivElement>(null);
  const fitRef = useRef<HTMLDivElement>(null);

  /*
    글자 수가 바뀔 때만 다시 잰다. `view` 자체를 보면 결재함에서 의견을 한 자 칠 때마다 재계산한다.

    <p>라벨·값만 추린다 — 적는 종이의 칸({@link DocField.input})은 React 요소라 통째로
    {@code JSON.stringify} 하면 순환 참조로 <b>화면이 죽는다</b>.
  */
  const contentKey = JSON.stringify([
    view.body,
    view.fields.map((f) => `${f.label}:${f.value}`),
    view.longs.map((f) => `${f.label}:${f.value}`),
    view.attachments,
    view.title,
    view.budget ?? null,
  ]);
  const minFit = view.budget ? BUDGET_MIN_FIT : MIN_FIT;

  useLayoutEffect(() => {
    const flow = flowRef.current;
    const fit = fitRef.current;
    // 적는 종이는 줄여 넣지 않는다 — 8할로 축소된 칸에 글을 치게 되고, 넘치면 종이가 길어지면 된다.
    if (!enabled || !flow || !fit) return;

    // 재기 전에 원래 크기로 되돌린다 — 줄여 둔 상태에서 재면 "이미 들어간다" 는 답만 나온다.
    fit.style.transform = '';
    fit.style.width = '';
    const available = flow.clientHeight;
    const needed = fit.scrollHeight;
    if (available <= 0 || needed <= available) return;

    const scale = Math.max(available / needed, minFit);
    fit.style.transformOrigin = 'top left';
    fit.style.transform = `scale(${scale})`;
    fit.style.width = `${100 / scale}%`;
  }, [contentKey, enabled, minFit]);

  return [flowRef, fitRef] as const;
}

/** 본문의 항목 표 — 흰 바탕·검은 선. 결재란과 같은 눈금이라 한 장으로 읽힌다. */
/** 값이 없는 칸에 세우는 표시. 빈 칸으로 두면 「덜 적었다」 로 읽힌다. */
const DASH = '-';

const docTable: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
  background: 'white',
  margin: '0 0 6px',
};

const docTableCell: React.CSSProperties = {
  border: '1px solid #333',
  padding: '5px 8px',
  verticalAlign: 'middle',
  /*
    한국어는 <b>어절</b>로 끊는다(`keep-all`). 글자 단위로 끊으면 「희망 검진기 / 관」 처럼
    낱말 한가운데가 갈라져 읽는 사람이 한 번 멈춘다 — 「희망 / 검진기관」 이 맞는 자리다.
    긴 영문·URL 처럼 어절이 칸보다 긴 경우에만 `overflowWrap` 이 끊어 준다.
  */
  wordBreak: 'keep-all',
  overflowWrap: 'break-word',
};

const docTableLabel: React.CSSProperties = {
  ...docTableCell,
  width: '128px',
  textAlign: 'center',
  fontWeight: 700,
  background: 'white',
};

const docTableValue: React.CSSProperties = { ...docTableCell, textAlign: 'left' };

/**
 * 회사 양식 표 (사용자 지시 2026-08-27 · `docs/samples/welfare/연차신청서예시.png`).
 *
 * <pre>
 *   소  속 │ …      │ 이  름 │ …    │ 직  급 │ …
 *   휴가종류│ …      │ 시작일 │ …    │ 종료일 │ …
 *   사  유 │ (전폭 — 여러 줄이 들어가는 큰 칸)
 * </pre>
 *
 * <h2>왜 표인가</h2>
 * 결재란은 이미 표다. 그 아래만 「1. 시작일 : 2026-09-14」 처럼 글줄이면 한 장이 두 문서처럼
 * 읽힌다. 회사가 쓰던 종이가 표였고, 인쇄해서 손으로 적어 넣던 자리도 그 칸이다.
 *
 * <h2>한 줄에 세 쌍</h2>
 * 짧은 값(날짜·구분·일수)이 각자 한 줄을 차지하면 표가 세로로 길어져 A4 한 장을 넘긴다.
 * 세 쌍씩 채우고 <b>남는 칸은 병합</b>한다 — 빈 칸을 남기면 아직 못 적은 자리처럼 보인다.
 *
 * <h2>바탕은 흰색이다</h2>
 * 원본 양식은 라벨 칸이 회색이지만 <b>흰 바탕에 검은 선만</b> 쓴다(사용자 지시). 회색은 흑백
 * 인쇄에서 뭉개지고, 화면에서는 결재란과 이 표가 서로 다른 문서처럼 보인다.
 */
function FormSheet({
  view,
  items,
  longs,
}: {
  view: DocView;
  items: DocField[];
  longs: DocField[];
}) {
  /** 기안자 정보 — 양식의 첫 줄. 서식 항목이 아니라 문서가 아는 값이라 여기서 붙인다. */
  const head: DocField[] = [
    { label: '소  속', value: view.drafterOrg ?? '' },
    { label: '이  름', value: view.drafterName },
    { label: '직  급', value: view.drafterRank ?? '' },
  ];

  const rows: DocField[][] = [];
  for (const pair of [...head, ...items]) {
    const last = rows[rows.length - 1];
    if (last && last.length < 3) last.push(pair);
    else rows.push([pair]);
  }

  return (
    <table style={docTable}>
      <colgroup>
        {[0, 1, 2].map((i) => (
          <Fragment key={i}>
            <col style={{ width: '13%' }} />
            <col style={{ width: '20.33%' }} />
          </Fragment>
        ))}
      </colgroup>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {row.map((pair, j) => (
              <Fragment key={j}>
                <th style={docTableLabel}>{pair.label}</th>
                {/* 줄 끝에서 짝이 모자라면 값 칸이 남은 폭을 다 먹는다 — 빈 칸을 남기지 않는다. */}
                {/*
                  적는 칸은 <b>위에 붙인다</b>. 한 칸에 긴 글이 들어가 줄이 높아졌을 때, 가운데
                  맞춤이면 옆의 짧은 칸들이 허공에 뜬 것처럼 보인다(사용자 지적 2026-09-29).
                */}
                <td
                  style={pair.input ? { ...docTableValue, verticalAlign: 'top' } : docTableValue}
                  colSpan={j === row.length - 1 ? (3 - row.length) * 2 + 1 : 1}
                >
                  {/* 빈 칸은 「-」 로 채운다 — 아무것도 없으면 아직 못 적은 자리인지 없는 값인지 모른다. */}
                  {pair.input ?? (pair.value || DASH)}
                </td>
              </Fragment>
            ))}
          </tr>
        ))}

        {longs.map((item) => (
          <tr key={item.label}>
            <th style={docTableLabel}>{item.label}</th>
            <td style={{ ...docTableValue, whiteSpace: 'pre-wrap', height: '110px', verticalAlign: 'top' }} colSpan={5}>
              {item.input ?? item.value}
            </td>
          </tr>
        ))}

        {/*
          번호가 붙는 <b>항목 표</b> — 협조전의 「1. 2. 3.」 (사용자 지시 2026-09-09).

          번호는 <b>여기서 매긴다</b>. 값으로 저장하지 않으므로 줄을 지우면 남은 줄이 곧바로
          1부터 다시 세어진다 — 「1. 3.」 같은 구멍이 종이에 남지 않는다.
        */}
      </tbody>
    </table>
  );
}

/**
 * 품목 표 — 잡자재 신청(DOC-139 §3.2). 회사 양식 표와 같은 선·글자로, 줄 번호 · 금액 · 합계는 그릴 때 센다.
 * 줄이 없어도 머리와 합계 줄은 선다 — 비어 있는 양식도 어디에 무엇을 적는지 보여야 한다(FormSheet 와 같은 규칙).
 */
export function ItemSheet({ table }: { table: { label: string; items: MaterialItem[]; total: number } }) {
  const cell = (align: 'left' | 'right' | 'center' = 'left'): React.CSSProperties => ({ ...docTableValue, textAlign: align });
  return (
    <table style={{ ...docTable, marginTop: '8px' }}>
      <colgroup>
        <col style={{ width: '6%' }} />
        <col style={{ width: '26%' }} />
        <col style={{ width: '28%' }} />
        <col style={{ width: '10%' }} />
        <col style={{ width: '14%' }} />
        <col style={{ width: '16%' }} />
      </colgroup>
      <thead>
        <tr>
          {['No', table.label, '규 격', '수 량', '단 가', '금 액'].map((head) => (
            <th key={head} style={{ ...docTableLabel, width: 'auto' }}>{head}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {(table.items.length > 0 ? table.items : [null]).map((item, i) => (
          <tr key={i}>
            <td style={cell('center')}>{item ? i + 1 : ''}</td>
            <td style={cell()}>{item?.name || DASH}</td>
            <td style={cell()}>{item?.spec || DASH}</td>
            <td style={cell('right')}>{item ? `${groupDigits(item.quantity)}${item.unit ? ` ${item.unit}` : ''}` : DASH}</td>
            <td style={cell('right')}>{item ? groupDigits(item.unitPrice) || DASH : DASH}</td>
            <td style={cell('right')}>{item ? groupDigits(String(materialItemAmount(item) ?? '')) || DASH : DASH}</td>
          </tr>
        ))}
        <tr>
          <th style={{ ...docTableLabel, width: 'auto' }} colSpan={5}>합 계</th>
          <td style={{ ...cell('right'), fontWeight: 700 }}>{groupDigits(String(table.total))}</td>
        </tr>
      </tbody>
    </table>
  );
}

/**
 * 협조전 본문 표 — <b>사용자가 보낸 양식 그대로</b> (2026-09-09).
 *
 * <pre>
 *   ┌──────────────────┬────────────────────────┬────────┐
 *   │            협 조 전                        │  비고  │
 *   ├──────────────────┼────────────────────────┼────────┤
 *   │ 1. 협조전 내용   │ 1) …                   │        │
 *   │                  │ 2) …                   │        │
 *   ├──────────────────┼────────────────────────┼────────┤
 *   │ 2. 현장 요청사항 │ 1) …                   │17일 도착│
 *   │                  │ 2) …                   │24일 도착│
 *   └──────────────────┴────────────────────────┴────────┘
 * </pre>
 *
 * <p>큰 항목 이름은 그 항목의 줄 수만큼 세로로 이어지고(병합), 비고는 <b>줄마다</b> 선다 —
 * 원본에서 「현장 요청사항」의 두 줄에 각각 도착일이 적혀 있던 그 자리다.
 *
 * <p>2026-10-02 부터 「기 한」 칸이 내용과 비고 사이에 선다(사용자 지시 「내용, 기한, 비고」).
 * 그림의 비고 자리 왼쪽에 같은 모양으로 하나 더 있다고 보면 된다.
 *
 * <p>줄이 하나도 없어도 표는 선다({@code rowTables} 가 빈 줄 하나를 세워 준다) — 기안 화면을
 * 처음 연 사람이 미리보기에서 <b>무엇을 어디에 적는 문서인지</b> 볼 수 있어야 한다.
 */
function CooperationSheet({ items }: { items: Array<{ content: string; due: string; note: string }> }) {
  return (
    <table style={{ ...docTable, marginTop: '4px' }}>
      <colgroup>
        {/* 번호 칸은 «두 자리 숫자만» 들어가면 된다 — 옆 여백까지 8px 씩 두면 빈 칸이 내용을 밀어낸다. */}
        <col style={{ width: '22px' }} />
        <col />
        {/* 기한은 날짜 한 토막이라 좁게, 비고는 한 줄 말이 들어가 조금 더(사용자 지시 2026-10-02). */}
        <col style={{ width: '15%' }} />
        <col style={{ width: '20%' }} />
      </colgroup>
      <tbody>
        <tr>
          <th colSpan={2} style={{ ...docTableLabel, letterSpacing: '8px', fontSize: '13px', padding: '5px 4px' }}>
            협 조 전
          </th>
          <th style={{ ...docTableLabel, fontSize: '13px', padding: '5px 4px' }}>기 한</th>
          <th style={{ ...docTableLabel, fontSize: '13px', padding: '5px 4px' }}>비 고</th>
        </tr>
        {(items.length > 0 ? items : [{ content: '', due: '', note: '' }]).map((item, i) => (
          <tr key={i}>
            <td style={{ ...docTableValue, padding: '5px 2px', textAlign: 'center', verticalAlign: 'top', color: '#555' }}>
              {i + 1}
            </td>
            <td style={{ ...docTableValue, whiteSpace: 'pre-wrap', verticalAlign: 'top' }}>{item.content}</td>
            <td style={{ ...docTableValue, whiteSpace: 'pre-wrap', verticalAlign: 'top', textAlign: 'center' }}>
              {item.due}
            </td>
            <td style={{ ...docTableValue, whiteSpace: 'pre-wrap', verticalAlign: 'top', textAlign: 'center' }}>
              {item.note}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * 표 아래 <b>맺음말</b> — 「위와 같은 건으로 결재를 요청합니다.」 (사용자 지시 2026-08-27).
 *
 * <p>표만 끝나면 문서가 <b>말없이 끊긴다</b>. 종이 결재에서 이 한 줄은 장식이 아니라 «요청» 이라는
 * 행위를 적은 것이고, 그것이 있어야 표가 서식이 아니라 <b>보낸 문서</b>로 읽힌다.
 *
 * <p>사람이 지우거나 고칠 수 없다. 문서마다 다르게 적히면 어느 것이 정식 표현인지가 흔들리고,
 * 빠뜨린 문서는 미완성으로 보인다.
 */
function ClosingLine() {
  return (
    <p style={{ textAlign: 'center', margin: '18px 0 0', fontSize: '13px', letterSpacing: '0.5px' }}>
      위와 같은 건으로 결재를 요청합니다.
    </p>
  );
}

export function ApprovalDocumentView({ view, domId }: { view: DocView; domId: string }) {
  const [frameRef, fit] = usePageZoom(!!view.budget);
  /** 사람이 고른 배율. {@code null} 이면 자동맞춤(한 장이 다 보이는 값)을 따라간다. */
  const [picked, setPicked] = useState<number | null>(null);
  const zoom = picked ?? fit.zoom;
  const { paperRef, panning, onMouseDown } = usePageViewer(frameRef, zoom, setPicked);
  /** 자동맞춤보다 키웠으면 종이가 액자를 넘친다 — 그때만 끌 수 있다. */
  const pannable = zoom > fit.zoom + 0.001;

  return (
    <div>
      {/* 배율 — 자동맞춤은 한 장이 다 보이는 대신 글씨가 작다. 읽을 때는 키우고, 「맞춤」 으로 한 장 보기로 돌아온다. */}
      <div className="flex items-center justify-end gap-1" style={{ marginBottom: '6px' }}>
        <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginRight: 'auto' }}>
          휠로 확대·축소 · 끌어서 이동
        </span>
        <button type="button" onClick={() => setPicked(stepZoom(zoom, -1))} style={zoomBtn} aria-label="축소">
          −
        </button>
        <span style={{ fontSize: '11px', color: 'var(--text-muted)', minWidth: '38px', textAlign: 'center' }}>
          {Math.round(zoom * 100)}%
        </span>
        <button type="button" onClick={() => setPicked(stepZoom(zoom, 1))} style={zoomBtn} aria-label="확대">
          ＋
        </button>
        <button
          type="button"
          onClick={() => setPicked(null)}
          style={{ ...zoomBtn, width: 'auto', padding: '2px 8px', color: picked == null ? 'var(--accent)' : undefined }}
        >
          맞춤
        </button>
      </div>

      {/*
        지면 액자 — 종이를 통째로 줄여 모달 한 화면에 앉힌다. 안쪽 문서는 언제나 실제 A4 크기 그대로다.
        키워서 종이가 액자보다 커지면 <b>여기서</b> 스크롤한다 — 모달 전체가 밀려 배율 단추까지 사라지지 않게.
      */}
      <div
        ref={frameRef}
        onMouseDown={onMouseDown}
        style={{
          width: '100%',
          maxHeight: fit.room > 0 ? `${fit.room}px` : undefined,
          overflow: 'auto',
          cursor: pannable ? (panning ? 'grabbing' : 'grab') : undefined,
          // 끄는 동안 글자가 파랗게 잡히면 종이를 옮기는 것이 아니라 고르는 것처럼 보인다.
          userSelect: panning ? 'none' : undefined,
        }}
      >
        <div
          ref={paperRef}
          style={{
            width: `${PAGE_BOX_W * zoom}px`,
            height: `${PAGE_BOX_H * zoom}px`,
            margin: '0 auto',
            overflow: 'hidden',
            // 흰 종이가 흰 모달에 묻히지 않게 테두리 한 줄 — 여기까지가 한 장이라는 표시다.
            boxShadow: '0 0 0 1px #e3e6ea',
          }}
        >
          <div style={{ width: `${PAGE_BOX_W}px`, transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
            <ApprovalPaper view={view} domId={domId} />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 종이 한 장 그 자체 — 액자·배율 없이 <b>A4 크기의 노드</b>다.
 *
 * <p>둘이 같은 컴포넌트를 쓴다: 읽는 종이(결재함·인쇄 미리보기)와 <b>적는 종이</b>(기안하기).
 * 나뉘어 있던 시절에는 기안 화면의 입력 카드와 미리보기 종이가 서로 다른 순서·다른 이름으로
 * 같은 값을 그렸고, 그 어긋남은 상신하고 종이를 뽑아 봐야 드러났다.
 *
 * @param grow 넘치는 만큼 <b>종이가 길어진다</b>. 적는 동안에는 줄여 넣지 않는다 —
 *             절반 크기로 축소된 칸에 글을 치게 되고, 한 장에 맞추는 일은 상신 전에 하면 된다
 */
export function ApprovalPaper({ view, domId, grow = false }: {
  view: DocView;
  domId: string;
  grow?: boolean;
}) {
  const paper = paperBoxes(view.boxes);
  const items = [...view.fields, ...view.longs];
  const [flowRef, fitRef] = useFitToPage(view, !grow);

  /*
    해외 경비 예산 — <b>엑셀 시트 그 자체</b>가 종이다(사용자 지시 2026-11-03 「로고·결재라인 빼고 순수 엑셀 시트로,
    이거만 예외」 → 「양식 그대로 가줘」). 레터헤드 · 문서 머리 · 맺음말 · 발신명의를 세우지 않고, 결재칸도 엑셀 그림처럼
    「현장 결재」 · 「본사 결재」 두 개다(budgetApprovalCells). 현장 · 기간 · 통화 · 환율 입력은 종이 밖(OverseasBudgetControls)이다.
  */
  if (view.edit?.budget || view.budget) {
    return (
      <div
        id={domId}
        className={`bomi-doc${view.edit ? ' paper-fill' : ''}`}
        style={{
          background: '#fff',
          color: INK,
          width: '100%',
          maxWidth: `${PAGE_W_MM + SCREEN_PAD_X_MM * 2}mm`,
          ...(grow
            ? { minHeight: `${PAGE_H_MM + SCREEN_PAD_Y_MM * 2}mm` }
            : { height: `${PAGE_H_MM + SCREEN_PAD_Y_MM * 2}mm` }),
          margin: '0 auto',
          padding: `${SCREEN_PAD_Y_MM}mm ${SCREEN_PAD_X_MM}mm`,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Pretendard", "Malgun Gothic", "맑은 고딕", sans-serif',
          fontSize: '13px',
          lineHeight: 1.65,
        }}
      >
        <div ref={flowRef} style={grow ? { flex: 1 } : { flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div ref={fitRef}>
            {view.budget && (
              <>
                <BudgetSheetTitle budget={view.budget} />
                <BudgetApprovalBoxes boxes={view.boxes} />
              </>
            )}
            {view.edit?.budget ?? (view.budget && <OverseasBudgetSheet budget={view.budget} />)}
          </div>
        </div>
      </div>
    );
  }

  return (
            <div
              id={domId}
              /* 적는 종이의 칸은 테두리를 벗고 종이 글씨를 물려받는다(`.paper-fill` · index.css). */
              className={`bomi-doc${view.edit ? ' paper-fill' : ''}`}
              style={{
                background: '#fff',
                color: INK,
                width: '100%',
                maxWidth: `${PAGE_W_MM + SCREEN_PAD_X_MM * 2}mm`,
                // 내용이 적어도 <b>한 장은 한 장</b>이다 — 늘리는 것이 아니라 처음부터 A4 한 장이고, 글이 그 안에 앉는다.
                // 늘어나지도 않는다(=height): 넘치면 아래 useFitToPage 가 줄여 넣는다.
                ...(grow
                  ? { minHeight: `${PAGE_H_MM + SCREEN_PAD_Y_MM * 2}mm` }
                  : { height: `${PAGE_H_MM + SCREEN_PAD_Y_MM * 2}mm` }),
                margin: '0 auto',
                padding: `${SCREEN_PAD_Y_MM}mm ${SCREEN_PAD_X_MM}mm`,
                display: 'flex',
                flexDirection: 'column',
                fontFamily: '"Pretendard", "Malgun Gothic", "맑은 고딕", sans-serif',
                fontSize: '13px',
                lineHeight: 1.65,
              }}
            >
              {/* ① 레터헤드 — CI · WORLD BEST NO.1 · 주소줄 · 굵은 가로줄 */}
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '12px' }}>
                <img src={CI_SRC} alt="보미건설" style={{ height: '30px', width: 'auto', display: 'block' }} />
                {/*
                  CI 표어 — 「WORLD BEST」 는 <b>먹색</b>이고 적색은 「NO.1」 뿐이다(CI 원본). 온통 적색으로 칠하면
                  강조가 두 번이라 어느 쪽도 강조가 아니게 된다.

                  묶음은 두 줄 중 넓은 쪽만큼만 차지하고({@code fit-content}), 「NO.1」 은 그 폭의 가운데 선다.
                */}
                <div style={{ width: 'fit-content', flexShrink: 0, lineHeight: 1.05 }}>
                  <div style={{ fontSize: '11px', fontWeight: 800, color: INK, letterSpacing: '0.3px', whiteSpace: 'nowrap' }}>
                    {COMPANY.tagline.top}
                  </div>
                  <div style={{ fontSize: '30px', fontWeight: 800, color: BRAND_COLOR.red, textAlign: 'center' }}>
                    {COMPANY.tagline.bottom}
                  </div>
                </div>
              </div>
              {/*
                주소줄 오른쪽 끝, 「NO.1」 바로 아래가 <b>문서 종류</b>의 자리다
                (사용자 지시 2026-09-09 · 원본 협조전 레터헤드).

                머리의 「문서구분 :」 줄로 적으면 문서번호·시행일자·수신과 같은 무게로 읽힌다 —
                그것들은 <b>이 문서의 값</b>이고, 종류는 <b>이 종이가 무엇인가</b>라 층이 다르다.
                종이를 받아 든 사람이 가장 먼저 보는 것도 그 자리다.
              */}
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '10px', margin: '8px 0 4px' }}>
                <p style={{ margin: 0, fontSize: '11px', color: '#333' }}>{COMPANY.addressLine}</p>
                {view.boxLayout === 'COOPERATION' && (
                  <span
                    style={{
                      flexShrink: 0,
                      border: `1px solid ${LINE}`,
                      padding: '1px 8px',
                      fontSize: '11px',
                      letterSpacing: '4px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    협&#160;조&#160;전
                  </span>
                )}
              </div>
              <div style={{ borderTop: `3px solid ${BRAND_COLOR.rule}`, marginBottom: '10px' }} />

              {/*
                ② 문서 메타(좌) + 결재란(우)

                <b>왼쪽이 결재란 높이를 따라간다</b>(사용자 지시 2026-09-09: "좌측에 문서번호 시행일자
                수신 참조 제목에 각각 height의 정렬을 좀 주자").

                결재란은 칸 높이가 정해진 표라 늘 178px 안팎이고, 왼쪽은 글줄 다섯이라 130px 남짓이었다.
                그래서 왼쪽 글이 위에 몰리고 <b>아래가 통째로 비어</b> 종이가 반쯤 떠 보였다.
                {@code stretch} 로 키를 맞추고 줄 사이를 고르게 벌리면(space-between), 두 덩이의 아래끝이
                같은 선에서 끝난다 — 종이 양식에서 눈이 기대하는 모양이다.
              */}
              <div style={{ display: 'flex', alignItems: 'stretch', justifyContent: 'space-between', gap: '14px' }}>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  {/*
                    안내문을 적지 않는다 (사용자 지시 2026-09-15). 기안 화면은 상신 전에도 서버가
                    지어 준 <b>다음 번호</b>를 실어 보내므로({@code ApprovalService.previewDocNo})
                    이 자리는 언제나 번호다 — 번호를 못 받은 잠깐만 줄표가 선다.
                  */}
                  <MetaRow label="문서번호" value={view.docNo ?? '—'} />
                  <MetaRow label="시행일자" value={view.dateLabel} />
                  <MetaRow label="수 신" value={view.edit?.recipient ?? (view.recipient?.trim() || '내부품의')} />
                  <MetaRow label="참 조" value={view.edit?.references ?? (view.references ?? []).join(', ')} />
                  {/* 협조전은 종류가 레터헤드(NO.1 아래)에 있다 — 여기 또 적으면 같은 말이 두 번이다. */}
                  {view.boxLayout !== 'COOPERATION' && view.formName && view.formName !== '기안문' && (
                    <MetaRow label="문서구분" value={view.formName} />
                  )}
                  {/* 고정 간격을 두지 않는다 — 위의 space-between 이 남는 높이를 줄 사이로 나눈다. */}
                  <MetaRow
                    label="제 목"
                    value={
                      view.edit?.title ??
                      `${view.title}${view.title.trim().endsWith('건') ? '' : '의 건'}${view.urgency === 'URGENT' ? '  (긴급)' : ''}`
                    }
                    bold
                  />
                </div>
                <ApprovalBoxTable paper={paper} layout={view.boxLayout ?? 'STANDARD'} />
              </div>

              <div style={{ borderTop: `2px solid ${BRAND_COLOR.rule}`, margin: '10px 0 14px' }} />

              {/*
                ③④ 본문 — 레터헤드와 발신명의 사이에 <b>남는 자리를 다 쓰는</b> 칸이다({@code flex: 1}).
                내용이 없으면 이 칸이 그대로 빈 종이가 되고, 넘치면 {@link useFitToPage} 가 안쪽을 줄여 넣는다.
              */}
              {/* 적는 종이는 넘치는 만큼 아래로 자란다 — 여기서 잘라 내면 방금 친 글이 사라진다. */}
              <div ref={flowRef} style={grow ? { flex: 1 } : { flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <div ref={fitRef}>
                  {view.edit?.body ?? (view.body?.trim() && (
                    <p style={{ margin: '0 0 6px', whiteSpace: 'pre-wrap', textIndent: '12px' }}>{view.body}</p>
                  ))}

                  {/*
                    <b>양식은 값이 없어도 선다</b>(사용자 지시 2026-08-27). 기안하는 사람은 미리보기를
                    열어 두고 채워 나간다 — 표가 첫 글자에 나타났다가 지우면 사라지면, 무엇을 어디에
                    적는 문서인지 끝까지 알 수 없다.
                  */}
                  {/*
                    협조전은 <b>본문이 곧 표</b>다(사용자 제공 양식 2026-09-09). 소속·이름·직급 머리도,
                    「- 아 래 -」도, 맺음말도 없다 — 그 셋은 사내 품의 양식의 것이고, 협조전 종이에는
                    「협 조 전 | 비고」 머리 아래 큰 항목이 바로 이어진다.
                  */}
                  {view.boxLayout === 'COOPERATION' ? (
                    view.edit?.rows ?? <CooperationSheet items={(view.rows ?? [])[0]?.items ?? []} />
                  ) : (items.length > 0 || view.longs.length > 0) && (
                    <>
                      {/* 제목 줄과 붙어 있으면 머리와 본문이 한 덩이로 읽힌다 — 위를 넉넉히 띄운다. */}
                      <p style={{ textAlign: 'center', margin: '34px 0 14px', letterSpacing: '6px', fontSize: '13px' }}>
                        - 아 래 -
                      </p>
                      <FormSheet view={view} items={view.fields} longs={view.longs} />
                      {view.edit?.itemTable ?? (view.itemTable && <ItemSheet table={view.itemTable} />)}
                      <ClosingLine />
                    </>
                  )}

                  {/* 붙임 · 끝. — 본문 끝을 알리는 줄이라 발신명의가 아니라 <b>글 바로 뒤</b>에 붙는다. */}
                  <div style={{ marginTop: '18px', minHeight: '18px' }}>
                    {(view.attachments ?? []).length > 0 ? (
                      <p style={{ margin: 0 }}>
                        <span style={{ letterSpacing: '2px' }}># 붙 임 : </span>
                        {view.attachments!.join(', ')} {view.attachments!.length}부.&nbsp;&nbsp;끝.
                      </p>
                    ) : (
                      <p style={{ margin: 0 }}>끝.</p>
                    )}
                  </div>
                </div>
              </div>

              {/* ⑤ 슬로건 + 기안부서 — 발신명의는 글이 짧든 길든 <b>종이 아래</b>에 선다. */}
              <div style={{ marginTop: '18px', flexShrink: 0, textAlign: 'center' }}>
                <p style={{ margin: 0, fontSize: '12px', fontWeight: 700, letterSpacing: '1px' }}>{COMPANY.slogan}</p>
                <p style={{ margin: '2px 0 0', fontSize: '16px', fontWeight: 800, letterSpacing: '6px' }}>
                  {view.drafterOrg ?? COMPANY.name}
                </p>
              </div>
            </div>
  );
}

/** `문서번호:` 처럼 라벨 폭을 고정하고 글자를 양끝으로 벌린다 — 사내 양식의 `수    신:` 모양. */
function MetaRow({ label, value, bold }: { label: string; value: React.ReactNode; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', minHeight: '20px' }}>
      <span
        style={{
          width: '62px',
          flexShrink: 0,
          display: 'inline-block',
          textAlign: 'justify',
          textAlignLast: 'justify',
          fontSize: '13px',
        }}
      >
        {label}
      </span>
      <span>:</span>
      {/* 값 자리가 남은 폭을 다 쓴다 — 적는 종이에서는 여기 입력칸이 앉는다({@link DocEdit}). */}
      <span style={{ flex: 1, minWidth: 0, fontWeight: bold ? 700 : 400, wordBreak: 'break-all' }}>{value}</span>
    </div>
  );
}

/**
 * 서면 결재란 — 4행 고정(대표이사 / 본부장 / 팀장 / 담당) + 협조 칸.
 *
 * 대표이사 줄은 <b>머리(대표이사)와 서명칸이 나뉜</b> 두 줄짜리다. 전자결재선에 대표이사·본부장이
 * 없어도 칸을 지우지 않는다 — 그 빈 칸을 받으려고 인쇄하는 문서다.
 */
function ApprovalBoxTable({ paper, layout }: {
  paper: ReturnType<typeof paperBoxes>;
  layout: 'STANDARD' | 'COOPERATION';
}) {
  const coopBody = <CoopCells cells={paper.coop} />;
  const coopCell = (
    <>
      <td style={{ ...labelCell, width: '22px', writingMode: 'vertical-rl', letterSpacing: '6px', padding: '4px 2px' }} rowSpan={2}>
        협조
      </td>
      <td rowSpan={2} style={{ ...signCellStyle, width: '74px', verticalAlign: 'middle' }}>
        {coopBody}
      </td>
    </>
  );

  /*
    <b>협조전 결재란은 원본 양식의 칸 배치 그대로다</b>
    (사용자 지시 2026-09-09 · docs/samples/협조전 의 표 셀 좌표에서 그대로 옮겼다).

      임 원 │      │ 대 표 이 사
      팀 장 │      │   (서명)
      담 당 │      │ 협 │
      기 안 │      │ 조 │

    품의서 양식과 <b>모양 자체가 다르다.</b> 품의는 가로로 늘어선 네 칸(대표이사·본부장·팀장·담당)
    이고, 협조전은 <b>왼쪽에 세로로 쌓인 네 칸</b>(임원·팀장·담당·기안)에 오른쪽 위가 대표이사,
    오른쪽 아래가 협조다. 본부장 칸은 <b>없다</b>. 처음에 가로 다섯 칸으로 그렸던 것은 추출한
    글자만 보고 짐작한 것이라 틀렸다 — 셀의 행·열 좌표를 읽고 나서야 이 모양이 나왔다.

    임원 칸은 <b>비어서</b>, 대표이사 칸에는 <b>팀장의 서명</b>이 나간다 — 팀장 전결로 끝나는
    문서라 그 자리를 전결자가 맡고, 팀장 칸에는 「전결」만 남는다({@link paperBoxes}).
    팀장 칸에 「전결」 없이 <b>이름</b>이 있다면 종이가 아니라 <b>결재선이 잘못 짜인 것</b>이다
    (전결 지점이 지정되지 않은 문서다).

    ★ <b>칸 배치는 원본 양식대로, 칸 크기는 {@link SIGN_H} 대로</b>다(사용자 지시 2026-09-09
      "이 디자인은 앞으로 통일"). 어느 칸이 어디 있는지는 양식마다 다르지만, 한 칸의 키는 같아야
      한다 — 종전의 26px 는 이름과 도장 두 줄이 들어가기에 모자랐다.
  */
  if (layout === 'COOPERATION') {
    return (
      <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', flexShrink: 0 }}>
        <tbody>
          <tr>
            <td style={{ ...labelCell, width: '40px' }}>임 원</td>
            <SignCell cell={paper.division} width={74} height={SIGN_H.member} />
            {/* 이름표 칸은 다 같은 바탕색이다 — 여기만 흰 채로 두면 그 칸만 «비어 있는 것»처럼 보인다. */}
            <td colSpan={2} style={{ ...labelCell, width: '96px', letterSpacing: '2px', fontSize: '12px' }}>
              대 표 이 사
            </td>
          </tr>
          <tr>
            <td style={{ ...labelCell }}>팀 장</td>
            <SignCell cell={paper.team} width={74} height={SIGN_H.member} />
            {/* 품의서와 같은 칸을 쓴다 — 전결자가 여기 서면 「전결  김팀장」과 처리일이 함께 나와야 한다. */}
            <SignCell cell={paper.ceo} width={96} height={SIGN_H.member} colSpan={2} />
          </tr>
          <tr>
            <td style={{ ...labelCell }}>담 당</td>
            {/* 담당 — 실무 담당자 날인 자리. 전자결재선에 없으면 비워서 손으로 받는다. */}
            <td style={{ ...signCellStyle, width: '74px', height: `${SIGN_H.member}px` }} />
            {coopCell}
          </tr>
          <tr>
            <td style={{ ...labelCell }}>기 안</td>
            <SignCell cell={paper.staff} width={74} height={SIGN_H.member} />
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', flexShrink: 0 }}>
      <tbody>
        <tr>
          <td rowSpan={2} style={{ ...labelCell, width: '46px' }}>결 재</td>
          <td colSpan={3} style={{ ...headCell }}>대 표 이 사</td>
        </tr>
        {/*
          칸 높이 — 대표이사 <b>48px</b>, 아래 셋 <b>30px</b>(사용자 지시 2026-09-09).

          대표이사 칸만 큰 것은 종이에서 <b>도장이 들어가는 자리</b>이기 때문이다(아래 칸들과 같을
          이유가 없다). 아래 셋의 30px 는 <b>실질 하한</b>이다 — 이름 한 줄(12px)과 도장·처리일
          한 줄(10px)에 위아래 여백을 더하면 그만큼이고, 더 줄이면 {@code overflow: hidden} 이
          도장 줄을 잘라 «결재했는데 안 한 것처럼» 보이는 종이가 나온다.

          왼쪽 머리글은 이 높이를 <b>따라간다</b>(§문서 메타 + 결재란) — 여기 숫자를 바꾸면 왼쪽 줄
          간격이 함께 조절되므로, 이 표만 고치면 된다.
        */}
        <tr>
          <SignCell cell={paper.ceo} width={110} height={SIGN_H.ceo} />
          <td colSpan={2} style={{ ...signCellStyle, width: '110px', height: `${SIGN_H.ceo}px` }} />
        </tr>
        <tr>
          <td style={{ ...labelCell }}>본부장</td>
          <SignCell cell={paper.division} width={110} height={SIGN_H.member} />
          <td rowSpan={3} style={{ ...labelCell, width: '22px', writingMode: 'vertical-rl', letterSpacing: '10px', padding: '4px 2px' }}>
            협조
          </td>
          <td rowSpan={3} style={{ ...signCellStyle, width: '88px', verticalAlign: 'middle' }}>
            {coopBody}
          </td>
        </tr>
        <tr>
          <td style={{ ...labelCell }}>팀 장</td>
          <SignCell cell={paper.team} width={110} height={SIGN_H.member} />
        </tr>
        <tr>
          <td style={{ ...labelCell }}>담 당</td>
          <SignCell cell={paper.staff} width={110} height={SIGN_H.member} />
        </tr>
      </tbody>
    </table>
  );
}

/**
 * 서명 한 칸 — 이름(위) · 도장/처리일(아래). 미처리면 빈 칸 그대로 둔다(손서명 자리).
 *
 * <p><b>도장이 없어도 처리일은 붙는다.</b> 전결자의 칸이 그렇다 — 「전결」은 이름 줄로 올라가
 * ({@link paperBoxes}) 도장 칸이 비는데, 그렇다고 <b>언제 결재했는지</b>까지 사라지면 그 종이는
 * 날짜 없는 결재가 된다.
 */
/**
 * 협조(합의) 칸의 속 — <b>사람마다 한 줄</b>이다.
 *
 * 예전에는 이름을 쉼표로 이어 붙이고 도장은 «누구 것이든 하나»만 찍었다. 합의자가 셋이면 한 사람이
 * 누른 순간 셋 다 끝난 것처럼 보였다 — 합의는 결재 순서를 타지 않아 서로 다른 때에 눌리므로
 * (사용자 결정 2026-09-15), 누가 했고 누가 아직인지가 <b>결재란에 그대로</b> 보여야 한다.
 *
 * <p>아직 안 누른 사람은 <b>도장 자리를 비워 둔다</b> — 종이에서 빈 칸은 손서명 자리다.
 * 「대기」 같은 글씨를 찍으면 그 자리가 사라진다({@link paperBoxes} 와 같은 규칙).
 */
function CoopCells({ cells }: { cells: PaperCell[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
      {cells.filter((c) => c.name).map((cell, i) => {
        const date = stampDate(cell.decidedAt);
        // 서명이 있으면 결재 칸과 <b>같은 배치</b>다 — 왼쪽에 이름·날짜, 오른쪽에 손글씨.
        if (cell.signImage) {
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', textAlign: 'left' }}>
              <div style={{ flex: '0 0 50%', minWidth: 0, overflow: 'hidden', lineHeight: 1.1, whiteSpace: 'nowrap', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', fontWeight: 700 }}>{cell.name}</div>
                {cell.stamp === '반려' && <div style={{ fontSize: '9px', fontWeight: 700, color: '#b00' }}>반려</div>}
                {date && <div style={{ fontSize: '9px', color: '#555' }}>{date}</div>}
              </div>
              <img
                src={cell.signImage}
                alt=""
                style={{ flex: '0 0 50%', minWidth: 0, height: '26px', objectFit: 'contain', objectPosition: 'right center' }}
              />
            </div>
          );
        }
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '3px', lineHeight: 1.25 }}>
            <span style={{ fontSize: '11px', fontWeight: 700 }}>{cell.name}</span>
            {cell.stamp && (
              <span style={{ fontSize: '10px', fontWeight: 700, color: cell.stamp === '반려' ? '#b00' : '#1560c4' }}>
                {cell.stamp}
                {date && <span style={{ color: '#555', fontWeight: 400 }}> {date}</span>}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SignCell({ cell, width, height, colSpan }: {
  cell: PaperCell; width: number; height: number; colSpan?: number;
}) {
  const date = stampDate(cell.decidedAt);

  /*
    서명이 찍힌 칸은 <b>둘로 나뉜다</b> (사용자 지시 2026-09-22 — 「이름 날짜 좌측에 위아래로 두고
    우측 여백에 서명이 들어가게」). 왼쪽이 누가·언제이고, 오른쪽이 그 사람의 손글씨다.

    <p>이때 「승인」·「합의」 글씨는 <b>빼낸다</b> — 서명이 곧 그 말이다. 도장 글씨와 서명을 한 칸에
    다 넣으면 30px 안에서 셋이 자리를 다투고, 무엇보다 같은 사실을 두 번 적는 칸이 된다.
    다만 <b>「반려」는 남긴다</b>: 서명은 「내가 보았다」까지만 말하고, 되돌렸다는 사실은 글씨만이
    말할 수 있다(그래서 반려에는 애초에 서명을 찍지 않는다 — 옛 문서를 위한 자리다).
  */
  if (cell.signImage) {
    return (
      <td
        colSpan={colSpan}
        style={{ ...signCellStyle, width: `${width}px`, height: `${height}px`, textAlign: 'left', padding: '1px 2px' }}
      >
        {/*
          <b>이름을 덮지 않는다</b> (사용자 지적 2026-09-22 — 「이름을 먹어버리네」). 한때 칸 전체에
          깔아 보았는데, 폭이 넓은 서명이 이름·날짜 위로 올라타 누구의 결재인지 읽히지 않았다.

          <p><b>가로는 정확히 반반</b>이다 (사용자 지시 2026-09-29). 예전에는 이름이 내용 폭만큼
          먹고 서명이 «남는 폭 전부»를 썼는데, 그러면 <b>이름 글자 수가 서명 크기를 정한다</b> —
          같은 판이 두 글자 이름 옆에서는 84px, 네 글자 옆에서는 61px 로 나가 종이마다 서명이
          달라 보였다. 반반으로 못 박으면 서명 크기가 사람 이름과 무관해진다.
          높이는 그대로 칸을 꽉 채운다 — 가로 상한을 두면 그 자리마저 반만 쓰게 된다.
        */}
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {/* 이름과 날짜는 <b>서로 가운데</b>로 선다 — 글자 수가 달라 왼쪽에 맞추면 날짜가 이름 밑에서 비뚤어진다. */}
          <div style={{ flex: '0 0 50%', minWidth: 0, overflow: 'hidden', lineHeight: 1.1, whiteSpace: 'nowrap', textAlign: 'center' }}>
            {cell.name && <div style={{ fontSize: '12px', fontWeight: 700 }}>{cell.name}</div>}
            {cell.stamp === '반려' && <div style={{ fontSize: '10px', fontWeight: 700, color: '#b00' }}>반려</div>}
            {date && <div style={{ fontSize: '10px', color: '#555' }}>{date}</div>}
          </div>
          <img
            src={cell.signImage}
            alt=""
            style={{
              flex: '0 0 50%',
              minWidth: 0,
              height: `${height - 4}px`,
              objectFit: 'contain',
              objectPosition: 'right center',
            }}
          />
        </div>
      </td>
    );
  }

  return (
    <td colSpan={colSpan} style={{ ...signCellStyle, width: `${width}px`, height: `${height}px` }}>
      {cell.name && <div style={{ fontSize: '12px', fontWeight: 700 }}>{cell.name}</div>}
      {(cell.stamp || date) && (
        <div style={{ fontSize: '10px', fontWeight: 700, color: cell.stamp === '반려' ? '#b00' : '#1560c4' }}>
          {cell.stamp}
          {date && <span style={{ color: '#555', fontWeight: 400 }}>{cell.stamp ? ` ${date}` : date}</span>}
        </div>
      )}
    </td>
  );
}

/** 배율 단추 — 지면 위에 얹히는 도구라 문서 색이 아니라 화면 테마 색을 쓴다(인쇄에는 실리지 않는다). */
const zoomBtn: React.CSSProperties = {
  width: '24px',
  height: '22px',
  fontSize: '13px',
  lineHeight: '20px',
  border: '1px solid var(--border)',
  borderRadius: '6px',
  background: 'var(--bg-hover)',
  color: 'var(--text-secondary)',
};

const labelCell: React.CSSProperties = {
  border: `1px solid ${LINE}`,
  background: BRAND_COLOR.boxLabelBg,
  textAlign: 'center',
  verticalAlign: 'middle',
  fontSize: '12px',
  padding: '4px 3px',
  whiteSpace: 'nowrap',
};
/**
 * <b>결재란 규격 — 앞으로 만드는 모든 결재란이 이 값을 쓴다</b> (사용자 지시 2026-09-09:
 * "이 디자인은 앞으로 통일인거야").
 *
 * <h2>왜 상수인가</h2>
 * 높이를 마크업에 흩어 두면 결재란이 하나 더 생길 때(협조전이 그랬다) 그 자리에서 다시 정하게 되고,
 * 그때부터 <b>같은 회사 양식이 문서마다 다른 키</b>로 나간다. 숫자를 여기 모아 두면 「통일」이
 * 규칙이 아니라 코드가 된다 — 바꿀 때도 이 줄만 고친다.
 *
 * <h2>왜 이 값인가</h2>
 * <ul>
 *   <li><b>대표이사 48px</b> — 도장이 들어가는 자리라 아래 칸들과 같을 이유가 없다.</li>
 *   <li><b>나머지 30px</b> — 실질 하한이다. 이름 한 줄(12px)과 도장·처리일 한 줄(10px)에 위아래
 *       여백을 더하면 그만큼이고, 더 줄이면 {@code overflow: hidden} 이 도장 줄을 잘라
 *       <b>결재했는데 안 한 것처럼</b> 보이는 종이가 나온다.</li>
 * </ul>
 *
 * <p>왼쪽 머리글(문서번호·시행일자·수신·참조·제목)은 이 높이를 <b>따라간다</b> — 여기만 고치면
 * 왼쪽 줄 간격이 함께 맞는다.
 */
const SIGN_H = { ceo: 48, member: 30 } as const;

/**
 * 결재란의 <b>이름표 줄</b>(「대 표 이 사」).
 *
 * 배경을 {@code labelCell}(「결 재」·본부장·팀장·담당)과 <b>같게</b> 둔다(사용자 지시 2026-09-09).
 * 흰 채로 두면 그 칸만 «서명이 들어갈 자리»처럼 보여, 실제로 이름이 들어가는 아래 칸과 구분이 안 된다 —
 * 이름표와 서명칸을 색으로 가르는 것이 이 표의 규칙이다.
 */
const headCell: React.CSSProperties = {
  border: `1px solid ${LINE}`,
  background: BRAND_COLOR.boxLabelBg,
  textAlign: 'center',
  fontSize: '13px',
  letterSpacing: '5px',
  padding: '3px 4px',
  whiteSpace: 'nowrap',
};
const signCellStyle: React.CSSProperties = {
  border: `1px solid ${LINE}`,
  textAlign: 'center',
  verticalAlign: 'middle',
  padding: '2px 3px',
  overflow: 'hidden',
};
